/**
 * Centralized layout style definitions for HCO (Healthcare Organization) pages
 *
 * These layout classes are used across HCO feature components for consistent spacing and layout.
 * All spacing values are defined here to maintain visual consistency and enable easy updates.
 *
 * NOTE: These styles are applied at the page component level, not in App.tsx, because:
 * - Many pages need custom layouts (drawers, height overrides, width constraints)
 * - Pages create their own wrapper structures that override App.tsx defaults
 * - This approach provides flexibility while maintaining consistency through shared constants
 */

/**
 * Main content area styles
 * Used for Tailwind-styled pages (v2 and newer Tailwind pages) that use <main> elements
 * Provides complete layout solution for scrollable content areas:
 * - flex-1: takes remaining vertical space
 * - overflow-y-auto: enables vertical scrolling
 * - mx-auto: centers horizontally
 * - mt-3: top margin (0.75rem / 12px)
 * - pl-9: left padding (2.25rem / 36px) on desktop
 * - pr-4: right padding (1rem / 16px) on desktop
 * - px-3: equal left/right padding (0.75rem / 12px) on mobile
 * - w-full: full width
 *
 * Usage: <main className={MAIN_CONTENT_AREA}>...</main>
 *
 * use this style for all v2 pages, should be the default moving forward
 */
export const MAIN_CONTENT_AREA =
  'tw:flex-1 tw:overflow-y-auto tw:mx-auto tw:mt-3 tw:max-lg:px-3 tw:pl-9 tw:pr-4 tw:w-full';

/**
 * Sticky-header table lists — every v2 list page in table view, on all three portals.
 * Three class changes per page, with `const tableScrolls = displayType !== 'card'`:
 *   <main className={clsx(MAIN_CONTENT_AREA, tableScrolls && MAIN_CONTENT_AREA_FIXED)}>
 *     ...header / filters...
 *     <div className={clsx('tw:mt-3', tableScrolls && TABLE_SCROLL_BOX)}>
 *       ...count line, <Table stickyHeader={tableScrolls}>, infinite-scroll sentinel...
 *
 * MAIN_CONTENT_AREA_FIXED stops the page area scrolling and hands its height to the list
 * body below. Card view leaves all three off and scrolls the page area as before.
 */
export const MAIN_CONTENT_AREA_FIXED = 'tw:flex tw:flex-col tw:!overflow-hidden';

/**
 * The list body of a sticky-header table list: the one box that scrolls, both ways, so
 * `<Table stickyHeader>` can keep its header row at the top of it.
 */
export const TABLE_SCROLL_BOX = 'tw:min-h-0 tw:flex-1 tw:overflow-auto';

/**
 * MAIN_CONTENT_AREA_FIXED for a list page whose <main> is the page root, with no drawer
 * column around it to bound its height: caps it at the viewport height under the top
 * bar (less its own top margin), like the drawer lists' outer container does. A max
 * height, not a height: <main> is a flex item there and a plain height loses to its
 * `flex-1` basis.
 */
export const MAIN_CONTENT_AREA_FIXED_STANDALONE = `${MAIN_CONTENT_AREA_FIXED} tw:max-h-[calc(100vh-72px-0.75rem)]`;

/**
 * Page header section styles
 * Used for header sections with title and action buttons
 * - flex: flexbox layout
 * - items-center: vertically center items
 * - justify-between: space items apart horizontally
 * - mb-3: bottom margin (0.75rem / 12px)
 */
export const PAGE_HEADER = 'tw:flex tw:items-center tw:justify-between tw:mb-3';

/**
 * Page header section styles with vertical margin
 * Used when header needs both top and bottom margin
 * - flex: flexbox layout
 * - items-center: vertically center items
 * - justify-between: space items apart horizontally
 * - my-3: vertical margin (0.75rem / 12px top and bottom)
 */
export const PAGE_HEADER_WITH_VERTICAL_MARGIN =
  'tw:flex tw:items-center tw:justify-between tw:mb-3';
